import React, { useEffect, useState, useRef } from "react";
import { Form, Button, Image, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  payOutBankStart,
  initiateDepositStart,
} from "../../store/slices/PayoutSlice";
import { Formik, Form as FORM, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { ButtonLoader } from "../Helper/Loader";
import { useTranslation } from "react-multi-lang";
import { apiUrl } from "../../Environment";
import { customAlphabet } from "nanoid";
import { numbers } from "nanoid-dictionary";
import { useDropzone } from "react-dropzone";
import { getErrorNotificationMessage } from "../Helper/NotificationMessage";

const RequestDepositPayoutModal = (props) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const t = useTranslation("request_deposit");
  const profile = useSelector((state) => state.admin.profile);
  const bankAccountList = useSelector((state) => state.payout.payOutBank);
  const requestPayOut = useSelector((state) => state.payout.initiateDeposit);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [skipRender, setSkipRender] = useState(true);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  const customStyles = {
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      left: "0px",
      borderRadius: "8px",
      overflow: "hidden",
      minWidth: "100px",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      minWidth: 250,
      fontSize: "0.85em",
      "&::-webkit-scrollbar-track": {
        boxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
        borderRadius: "3px",
        backgroundColor: "#fff",
      },
      "&::-webkit-scrollbar": {
        width: "4px",
        backgroundColor: "#fff",
      },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: "3px",
        boxShadow: "inset 0 0 6px rgba(0, 0, 0, .3)",
        backgroundColor: "#555",
      },
    }),
    container: (provided) => ({ ...provided, width: "auto" }),
    control: (provided) => ({
      ...provided,
      backgroundColor: "transparent!important",
      border: "1px solid #E7E7E7!important",
      borderRadius: "8px!important",
      boxShadow: "none!important",
      height: "45px",
      cursor: "pointer",
      minWidth: "auto",
      fontSize: "0.9em",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#50506B80",
      fontSize: "1em",
      fontWeight: "400",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#000",
      display: "flex",
      alignItems: "center",
      gap: "0.5em",
      fontSize: "0.9em",
      fontWeight: "600",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      color: "#DDE1E6!important",
    }),
    indicatorContainer: (provided) => ({
      ...provided,
      fill: "#DDE1E6!important",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      svg: {
        fill: "#50506B80",
      },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isSelected ? "#f7f7f7" : "#fff",
        color: "#000",
      };
    },
  };

  const [files, setFiles] = useState([]);
  const [showMessage, setShowMessage] = useState(false);

  const { getRootProps, getInputProps, isFocused, isDragActive, isDragReject } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpg": [".jpg"],
      "image/jpeg": [".jpeg"],
    },
    maxFiles: 1,
    useFsAccessApi: false,
    onDrop: (acceptedFiles) => {
      setFiles(
        acceptedFiles.map(file => Object.assign(file, {
          preview: URL.createObjectURL(file)
        }))
      )
    },
  });

  const generateUniqueId = () => {
    const nanoid = customAlphabet(numbers, 18);
    return nanoid();
  };

  const validationSchema = Yup.object().shape({
    amount: Yup.string()
      .test(t("create_payment.amount.invalid"), (value) => {
        if (!value) return true;
        else {
          return !isNaN(value);
        }
      })
      .required(t("requested_amount.required"))
      .min(1, t("amount_must_be_at_least"))
      .test('min', t('amount_must_be_at_least'), (value) => {
        return value > 0;
      })
      .test('max', t('amount_must_be_at_most'), (value) => {
        return value <= 10000000;
      }),
    payout_bank_account_id: Yup.string().required(t("bank_account.required")),
    utr_number: Yup.string().required(t("utr_number.required")),
    payment_picture: Yup.mixed().required(t("file.required")),
  });

  useEffect(() => {
    if (!profile.loading && Object.keys(profile.data).length > 0) {
      dispatch(payOutBankStart());
    }
  }, [profile]);

  useEffect(() => {
    if (
      !bankAccountList.loading &&
      Object.keys(bankAccountList.data).length > 0 &&
      bankAccountList.data.bank_accounts.length > 0
    ) {
      let acc = bankAccountList.data.bank_accounts.map((acc) => ({
        label: acc.account_holder_name + " - " + acc.account_number.slice(-4) + " - " + acc.bank_name,
        value: acc,
      }));
      setBankAccounts(acc);
    }
  }, [bankAccountList]);

  const handleSubmit = (values) => {
    let updatedValues = { ...values, payment_picture: files[0]};
    // if (!updatedValues.payment_picture) {
    //   delete updatedValues.payment_picture;
    // }
    updatedValues = {
      ...updatedValues,
      callback_url: apiUrl + "payout_deposit_callback",
      is_central: 1,
    };
    dispatch(initiateDepositStart(updatedValues));
  };

  useEffect(() => {
    if (
      !skipRender &&
      !requestPayOut.loading &&
      Object.keys(requestPayOut.data).length > 0
    ) {
      props.closeRequestPayoutModal();
    }
    setSkipRender(false);
  }, [requestPayOut]);

  const removeFile = (fileIndex) => {
    const updatedFiles = files.filter((_, index) => index !== fileIndex);
    setFiles(updatedFiles);
  };

  return (
    <>
      <Modal
        className="modal-dialog-center payment-link-created-modal"
        size="md"
        centered
        show={props.requestPayout}
        onHide={props.closeRequestPayoutModal}
        backdrop="static"
      >
        <Modal.Body>
          <Button
            onClick={() => props.closeRequestPayoutModal()}
            className="modal-close"
          >
            <Image
              className="close-icon"
              src={window.location.origin + "/img/close-modal-icon.svg"}
            />
          </Button>
          <div className="request-payout-sec">
            <h3>{t("heading")}</h3>

            <Formik
              initialValues={{
                amount: "",
                order_id: `OD${generateUniqueId()}`,
                merchant_email: "",
                payout_bank_account_id: "",
                utr_number: "",
                callback_url: "",
                payment_picture: null,
              }}
              validationSchema={validationSchema}
              innerRef={formRef}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, setFieldTouched, errors }) => (
                <FORM className="bank-account-form">
                  <Form.Group
                    className="mb-3"
                    controlId="exampleForm.ControlInput1"
                  >
                    <Form.Label>
                      {t("requested_amount.label")}
                      <span class="text-danger"> *</span>
                    </Form.Label>
                    <Field
                      className="form-control"
                      placeholder={t("requested_amount.placeholder")}
                      name="amount"
                      disabled={values.amount_type === 2}
                      type="text" 
                      pattern="\d*" 
                      onKeyDown={(e) => {
                        if (!/[0-9]|Backspace|ArrowLeft|ArrowRight|Delete/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <ErrorMessage
                      component={"div"}
                      name="amount"
                      className="errorMsg w-100"
                    />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label>
                      {t("bank_account.label")}{" "}
                      <span class="text-danger"> *</span>
                    </Form.Label>
                    <Select
                      options={bankAccounts}
                      styles={customStyles}
                      isSearchable={false}
                      onChange={(selectedOption) => {
                        setFieldValue(
                          "payout_bank_account_id",
                          selectedOption.value.payout_bank_account_id
                        );
                        setSelectedBankAccount(selectedOption.value);
                      }}
                      placeholder={<div>{t("bank_account.placeholder")}</div>}
                    />
                    <ErrorMessage
                      component={"div"}
                      name="payout_bank_account_id"
                      className="errorMsg w-100"
                    />
                  </Form.Group>
                  <Form.Group>
                    {selectedBankAccount && (
                      <div>
                        <p className="mb-3">
                          <strong className="text-secondary fw-bold">
                            {t("account_name")} :{" "}
                          </strong>
                          <span className="text-dark">
                            {selectedBankAccount.account_holder_name}
                          </span>
                        </p>
                        <p className="mb-3">
                          <strong className="text-secondary fw-bold">
                           {t("bank_name")}:{" "}
                          </strong>
                          <span className="text-dark">
                            {selectedBankAccount.bank_name}
                          </span>
                        </p>
                        <p className="mb-3">
                          <strong className="text-secondary fw-bold">
                           {t("account_number")}:{" "}
                          </strong>
                          <span className="text-dark">
                            {selectedBankAccount.account_number}
                          </span>
                        </p>
                        <p className="mb-3">
                          <strong className="text-secondary fw-bold">
                          {t("ifsc_code")}:{" "}
                          </strong>
                          <span className="text-dark">
                            {selectedBankAccount.ifsc_code}
                          </span>
                        </p>
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group
                    className="mb-3"
                    controlId="exampleForm.ControlInput1"
                  >
                    <Form.Label>
                      {t("utr_number.label")}
                      <span class="text-danger"> *</span>
                    </Form.Label>
                    <Field
                      className="form-control"
                      placeholder={t("utr_number.placeholder")}
                      name="utr_number"
                      type="text"
                      disabled={values.amount_type === 2}
                    />
                    <ErrorMessage
                      component={"div"}
                      name="utr_number"
                      className="errorMsg w-100"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{t("paytment_screenshot")}</Form.Label>
                    {files.length <= 0 && (
                      <div className="withdrawal-logo-sec">
                        <div
                          className={`withdrawal-logo-sec-box ${
                            isDragActive ? "active" : ""
                          } ${isDragReject ? "reject" : ""}`}
                          {...getRootProps({
                            onDrop: (e) => {
                              setShowMessage(false);
                              let ext = e.dataTransfer.files[0].name.slice(
                                e.dataTransfer.files[0].name.lastIndexOf(".")
                              );
                              if (
                                e.dataTransfer.files.length == 1 && getInputProps().accept.includes(ext) && e.dataTransfer.files[0].type)
                                setFieldValue("payment_picture", e.dataTransfer.files);
                              else if (!getInputProps().accept.includes(ext))
                                getErrorNotificationMessage(t("file.invalid"));
                              else
                                getErrorNotificationMessage(t("file.single"));
                            },
                            onChange: (e) => {
                              let ext = e.target.files[0].name.slice((e.target.files[0].name.lastIndexOf(".")));
                              if (e.target.files.length == 1 && getInputProps().accept.includes(ext))
                                setFieldValue("payment_picture", e.target.files) 
                              else if (!getInputProps().accept.includes(ext))
                                getErrorNotificationMessage(t("file.invalid"));
                              else
                                getErrorNotificationMessage(t("file.single"))
                            },
                          })}
                        >
                          <input name="file" {...getInputProps()} />
                          <div className="onboarding-document-item">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="35"
                              height="35"
                              fill="none"
                              viewBox="0 0 37 38"
                            >
                              <g
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.525"
                                clipPath="url(#clip0_455_5823)"
                              >
                                <path
                                  stroke="#048A89"
                                  strokeOpacity="1"
                                  d="M24.792 24.871l-6.1-6.099-6.098 6.1"
                                ></path>
                                <path
                                  stroke="#048A89"
                                  d="M18.691 18.772v13.723M31.485 28.515a7.624 7.624 0 00-3.644-14.317H25.92a12.198 12.198 0 10-20.95 11.13"
                                ></path>
                                <path
                                  stroke="#048A89"
                                  d="M24.792 24.871l-6.1-6.099-6.098 6.1"
                                ></path>
                              </g>
                              <defs>
                                <clipPath id="clip0_455_5823">
                                  <path
                                    fill="#fff"
                                    d="M0 0H36.594V36.594H0z"
                                    transform="translate(.396 .475)"
                                  ></path>
                                </clipPath>
                              </defs>
                            </svg>
                            <p>
                              {t("file.placeholder")} <br></br>{" "}
                              {t("file.supported")} <br></br> {t("file.drag")}{" "}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <ErrorMessage
                      name="payment_picture"
                      component={"div"}
                      className="errorMsg w-100x"
                    />
                    {files &&
                      files.map((file, index) => (
                        <div className="upload-preview-sec" key={file.name}>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 18 22"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M17.0297 5.76641C17.1703 5.90703 17.25 6.09687 17.25 6.29609V20.75C17.25 21.1648 16.9148 21.5 16.5 21.5H1.5C1.08516 21.5 0.75 21.1648 0.75 20.75V1.25C0.75 0.835156 1.08516 0.5 1.5 0.5H11.4539C11.6531 0.5 11.8453 0.579688 11.9859 0.720313L17.0297 5.76641ZM15.5203 6.64062L11.1094 2.22969V6.64062H15.5203ZM4.5 10.2969C4.45027 10.2969 4.40258 10.3166 4.36742 10.3518C4.33225 10.387 4.3125 10.4346 4.3125 10.4844V11.6094C4.3125 11.6591 4.33225 11.7068 4.36742 11.742C4.40258 11.7771 4.45027 11.7969 4.5 11.7969H13.5C13.5497 11.7969 13.5974 11.7771 13.6326 11.742C13.6677 11.7068 13.6875 11.6591 13.6875 11.6094V10.4844C13.6875 10.4346 13.6677 10.387 13.6326 10.3518C13.5974 10.3166 13.5497 10.2969 13.5 10.2969H4.5ZM4.5 13.4844C4.45027 13.4844 4.40258 13.5041 4.36742 13.5393C4.33225 13.5745 4.3125 13.6221 4.3125 13.6719V14.7969C4.3125 14.8466 4.33225 14.8943 4.36742 14.9295C4.40258 14.9646 4.45027 14.9844 4.5 14.9844H8.8125C8.86223 14.9844 8.90992 14.9646 8.94508 14.9295C8.98025 14.8943 9 14.8466 9 14.7969V13.6719C9 13.6221 8.98025 13.5745 8.94508 13.5393C8.90992 13.5041 8.86223 13.4844 8.8125 13.4844H4.5Z"
                              fill="#048A89"
                            />
                          </svg>
                          <div className="w-100">
                            <p className="file-upload-name">{file.name}</p>
                            <span className="file-upload-size">
                              {file.size >= 1024
                                ? (file.size / (1024 * 1024)).toFixed(2) + " MB"
                                : (file.size / 1024).toFixed(2) + " KB"}
                            </span>
                          </div>
                          <div
                            onClick={() => {
                              removeFile(index);
                              setFieldValue("payment_picture", null);
                            }}
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1ZM8 7.151L6.374 5.525C6.26142 5.41242 6.10872 5.34917 5.9495 5.34917C5.79028 5.34917 5.63758 5.41242 5.525 5.525C5.41242 5.63758 5.34917 5.79028 5.34917 5.9495C5.34917 6.10872 5.41242 6.26142 5.525 6.374L7.151 8L5.525 9.626C5.46925 9.68175 5.42503 9.74793 5.39486 9.82076C5.36469 9.8936 5.34917 9.97166 5.34917 10.0505C5.34917 10.1293 5.36469 10.2074 5.39486 10.2802C5.42503 10.3531 5.46925 10.4193 5.525 10.475C5.58075 10.5307 5.64693 10.575 5.71976 10.6051C5.7926 10.6353 5.87066 10.6508 5.9495 10.6508C6.02834 10.6508 6.1064 10.6353 6.17924 10.6051C6.25207 10.575 6.31825 10.5307 6.374 10.475L8 8.849L9.626 10.475C9.68175 10.5307 9.74793 10.575 9.82076 10.6051C9.8936 10.6353 9.97166 10.6508 10.0505 10.6508C10.1293 10.6508 10.2074 10.6353 10.2802 10.6051C10.3531 10.575 10.4193 10.5307 10.475 10.475C10.5307 10.4193 10.575 10.3531 10.6051 10.2802C10.6353 10.2074 10.6508 10.1293 10.6508 10.0505C10.6508 9.97166 10.6353 9.8936 10.6051 9.82076C10.575 9.74793 10.5307 9.68175 10.475 9.626L8.849 8L10.475 6.374C10.5307 6.31825 10.575 6.25207 10.6051 6.17924C10.6353 6.1064 10.6508 6.02834 10.6508 5.9495C10.6508 5.87066 10.6353 5.7926 10.6051 5.71976C10.575 5.64693 10.5307 5.58075 10.475 5.525C10.4193 5.46925 10.3531 5.42503 10.2802 5.39486C10.2074 5.36469 10.1293 5.34917 10.0505 5.34917C9.97166 5.34917 9.8936 5.36469 9.82076 5.39486C9.74793 5.42503 9.68175 5.46925 9.626 5.525L8 7.151Z"
                                fill="#048A89"
                              />
                            </svg>
                          </div>
                        </div>
                    ))}
                  </Form.Group>
                  <div className="request-payout-payment-method-btn-sec">
                    <Button
                      className="snapgopay-cancel-btn"
                      onClick={() => props.closeRequestPayoutModal()}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      type="submit"
                      className="snapgopay-auth-btn"
                      disabled={requestPayOut.buttonDisable}
                    >
                      {requestPayOut.buttonDisable ? (
                        <ButtonLoader />
                      ) : (
                        t("deposit_btn.text")
                      )}
                    </Button>
                  </div>
                </FORM>
              )}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RequestDepositPayoutModal;
